import styled from '@emotion/styled'
import React from 'react'

import { Box } from '../Box/Box'
import { Text } from '../Text/Text'
import { Icon, IconProps } from '../Icon/Icon'

export interface ToggleProps {
  on: boolean
  onChange: () => void
  label?: string
  labelWeight?: 'regular' | 'strong' | 'stronger'
  align?: 'left' | 'justify' | 'right'
  disabled?: boolean
  width?: number
  showText?: boolean
  showIcons?: boolean
  toggleOnIcon?: IconProps['name']
  toggleOffIcon?: IconProps['name']
  toggleOnIconSize?: IconProps['size']
  toggleOffIconSize?: IconProps['size']
  toggleOnText?: string
  toggleOffText?: string
  toggleOnTone?: IconProps['tone']
  toggleOffTone?: IconProps['tone']
}

export const Toggle: React.FC<ToggleProps> = ({
  on,
  onChange,
  label,
  labelWeight = 'regular',
  align = 'left',
  disabled,
  width = 43,
  showText = true,
  showIcons = true,
  toggleOnIcon,
  toggleOffIcon,
  toggleOnIconSize = 'm',
  toggleOffIconSize = 'm',
  toggleOnText,
  toggleOffText,
  toggleOnTone = 'action',
  toggleOffTone = 'action'
}) => {
  return (
    <Box
      onClick={disabled ? undefined : onChange}
      display="flex"
      alignItems="center"
      flexDirection={align === 'left' ? undefined : 'row-reverse'}
      cursor={disabled ? 'not-allowed' : 'pointer'}
      height={24}
      opacity={disabled ? 0.4 : 1} // Opacity fades out the entire toggle when disabled
    >
      <Box position="relative" className="toggle--wrapper" height="24px" width={width}>
        {showText && toggleOnText && toggleOffText && (
          <Box
            component={'span'}
            position="relative"
            zIndex={2}
            height="100%"
            width={width - 37}
            left={on ? 10 : 27}
            top={'auto'}
            display="flex"
            alignItems={'center'}
          >
            <Text size={'tiny'} tone={on ? 'ghost' : 'light'} weight="stronger" align={'center'}>
              {on ? toggleOnText : toggleOffText}
            </Text>
          </Box>
        )}

        <StyledInput
          component="input"
          type="checkbox"
          checked={on}
          className="toggle--track"
          border={on ? 'none' : `1px solid var(--colors-${toggleOffTone}--light)`}
          background={on ? `var(--colors-${toggleOnTone})` : `var(--colors-background)`}
          width={width}
          height={24}
          radius="xl"
          position="absolute"
          zIndex={0}
          top="50%"
          transform="translateY(-50%)"
          disabled={disabled}
          readOnly
        />
        <Box
          className="toggle--indicator"
          background={on ? `var(--colors-ghost)` : `var(--colors-${toggleOffTone})`}
          position="absolute"
          zIndex={1}
          radius="circle"
          left={3}
          top="50%"
          height={18}
          width={18}
          transform={on ? `translateY(-50%) translateX(${width - 24}px)` : 'translateY(-50%)'}
          transition="var(--transition)"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {showIcons && toggleOnIcon && toggleOffIcon && (
            <Icon
              name={on ? toggleOnIcon : toggleOffIcon}
              size={on ? toggleOnIconSize : toggleOffIconSize}
              tone={on ? toggleOnTone : toggleOffTone}
            />
          )}
        </Box>
      </Box>

      {label && (
        <Box
          className="toggle--label"
          userSelect="none"
          flexGrow={align === 'justify' ? 1 : undefined}
          paddingLeft={align === 'left' ? 's' : undefined}
          paddingRight={align === 'right' ? 's' : undefined}
        >
          <Text weight={labelWeight}>{label}</Text>
        </Box>
      )}
    </Box>
  )
}

const StyledInput = styled(Box)`
  appearance: none;
  margin: 0;
`
